import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { Config } from '../Config';
import { ReportLauncher } from '../reports/report-launcher';
import * as Driver from 'driver.js';
import 'driver.js/dist/driver.min.css';

@Component({
  selector: 'app-bridge-conditions-map',
  templateUrl: './bridge-conditions-map.component.html',
  styleUrls: ['./bridge-conditions-map.component.css']
})
export class BridgeConditionsMapComponent implements OnInit, AfterViewInit, OnDestroy {

  private driver: Driver;

  constructor(
    private readonly reportLauncher: ReportLauncher,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.driver = new Driver({
      allowClose: false,
      animate: true,
      opacity: 0.75,
      padding: 5,

      onHighlightStarted: (element) => {
        // Disable pointer events for the current step
        const highlightedElement = element.getNode();
        (highlightedElement as HTMLElement).style.pointerEvents = 'none';
      },
      onDeselected: (element) => {
        // Re-enable pointer events when the step ends
        const deselectedElement = element.getNode();
        (deselectedElement as HTMLElement).style.pointerEvents = '';
      },
    });
  }

  extentConfigs: any[] = [
    {
      toolTip: 'Statewide',
      icon: 'm28.531 12.656-.187-.531-1.156-.344-.344-.562-.187-1.437-.719-.312-.094-.344-.375-.312h-20.219v-1.625l-1.656.844 .219-.312-.656.5-1.375.594-.406 13.906 20.719.094h1.719l.313-.531.5-.25.781 .156 1.375-.562.094-.437 1.75-1.312-1.187-1.437h-.25l-.25-.875-.531-.062-.031-.75v-.375l.406-.25.188-.469-.375-.75.844-.844.719-1.25z',
      color: 'white',
      extent: {
        center: [-77.6362661695295, 40.92039887644528],
        zoom: 8
      },
      extentType: 'static',
      label: 'Statewide Extent'
    },
    {
      toolTip: 'Current Area of Interest',
      icon: 'icon-ui-locate',
      color: 'white',
      extent: async () => await this.getAoi(),
      extentType: 'async-func',
      label: 'AOI'
    }
  ];

  defaultBasemap = {
    'tileUrl': 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile',
    'tileTemplate': '/{level}/{row}/{col}',
    'title': 'Streets'
  };

  basemapList = 'terrain,streets,satellite,hybrid,topo,national-geographic,gray-vector,osm';

  basemapConfigs: any[] = [
    {
      'tileUrl': 'https://services.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile',
      'thumbnailTile': '/15/12358/9365',
      'tileTemplate': '/{level}/{row}/{col}',
      'title': 'Elevation'
    },
    {
      'tileUrl': 'https://gis.penndot.pa.gov/gis/rest/services/basemaps/penndotbasemap/MapServer/tile',
      'thumbnailTile': '/7/48/36',
      'tileTemplate': '/{level}/{row}/{col}',
      'title': 'PennDOT'
    },
    {
      'tileUrl': 'https://services.arcgisonline.com/arcgis/rest/services/USA_Topo_Maps/MapServer/tile',
      'thumbnailTile': '/7/48/36',
      'tileTemplate': '/{level}/{row}/{col}',
      'title': 'Topographic DRG'
    }
  ];

  @ViewChild('map', { static: false }) private mapRef: ElementRef<any>;
  @ViewChild('extent', { static: false }) private extentRef: ElementRef<any>;
  @ViewChild('basemapSwitcher', { static: false }) private basemapSwitcherRef: ElementRef<any>;
  @ViewChild('aoiWindow', { static: false }) private aoiWindowRef: ElementRef<any>;
  @ViewChild('legendWindow', { static: false }) private legendWindowRef: ElementRef<any>;
  @ViewChild('legend', { static: false }) private legendRef: ElementRef<any>;
  @ViewChild('identify', { static: false }) private identifyRef: ElementRef<any>;
  @ViewChild('customIdentifyWindow', { static: false }) private customIdentifyWindowRef: ElementRef<any>;

  public config = (window as any).config as Config;
  layerService: any;
  readonly aoiTypesWithReport = ['state', 'district', 'county', 'houseDistrict', 'congressionalDistrict', 'senateDistrict', 'planningPartner', 'municipality'];
  enableConditionReportBtn = false;

  previouslyIdentifiedObjectId = '';
  selectedCustomFeature: {
    projectType: 'Local' | 'State' | 'AOI',
    overallCond: string,
    brKey: string,
    countyName: string,
    municName: string,
    locationName: string,
    featureCarried: string,
    featureIntersected: string,
    owner: string,
    length: string,
    deckArea: string,
    spansNumber: string,
    material: string,
    structureType: string,
    yearBuilt: string,
    postingStatus: string,
    wlSingle: string,
    wlCombo: string,
    wlOther: string,
    deckCondition: string,
    superCond: string,
    subCond: string,
    culvertCond: string,
    planningPartner: string,
    avgDailyTraffic: string,
    oneMapUrl: string,
    county: string,
    route: string,
    segment: string,
    offset: string,
    hasPrev: boolean,
    hasNext: boolean,
    bridgeId: string,
    penndotNearYouUrl: string
  } = {
      projectType: null,
      overallCond: '',
      brKey: '',
      countyName: '',
      municName: '',
      locationName: '',
      featureCarried: '',
      featureIntersected: '',
      owner: '',
      length: '',
      deckArea: '',
      spansNumber: '',
      material: '',
      structureType: '',
      yearBuilt: '',
      postingStatus: '',
      wlSingle: '',
      wlCombo: '',
      wlOther: '',
      deckCondition: '',
      superCond: '',
      subCond: '',
      culvertCond: '',
      planningPartner: '',
      avgDailyTraffic: '',
      oneMapUrl: '',
      county: '',
      route: '',
      segment: '',
      offset: '',
      hasPrev: false,
      hasNext: false,
      bridgeId: '',
      penndotNearYouUrl: ''
    };
  featureCount = 0;
  curFeatureIndex = 0;
  projectTitle = '';
  headerColor = '';

  isMobileView = false;
  zoomLevel = 7;
  expanderTopPx = '10px';
  aoiHeight = '';
  aoiWidth = '875';
  showMobileLegend = false;
  isLegendReady = false;
  showMobileIdentify = false;
  showLeftArrow = false;
  showRightArrow = false;

  activateAOITab: Subject<void> = new Subject<void>();
  readonly openVL: Subject<{ countyCode: string, stateRouteNumber: string, segmentNumber: string, offset?: string | number }> =
    new Subject<{ countyCode: string, stateRouteNumber: string, segmentNumber: string, offset?: string | number }>();
  readonly openSV: Subject<{ countyCode: string, stateRouteNumber: string, segmentNumber: string, offset?: string | number }> =
    new Subject<{ countyCode: string, stateRouteNumber: string, segmentNumber: string, offset?: string | number }>();
  readonly openSW: Subject<any> = new Subject<any>();
  readonly deeplinkPresent: Subject<void> = new Subject<void>();
  readonly aoiGo: Subject<void> = new Subject<void>();
  readonly returnAOIInfo: Subject<string> = new Subject<string>();
  readonly openOM: Subject<void> = new Subject<void>();

  async ngOnInit() {
    if (window.location.href.includes('?showMobile=true')) {
      this.isMobileView = true;
      this.zoomLevel = 6;
      this.expanderTopPx = '0';

      // force the mobile page to be landscape
      this.updateOrientationStyles();

      // Set aoi height
      this.aoiHeight = '100vh';
      this.aoiWidth = '100%';
    }
  }

  async ngAfterViewInit() {
    await this.setBaseMap();
    await this.setupBasemapSwitcher();
    await this.constructExtent();

    this.openAOI(true);

    await this.loadMapLayers();

    /* If deeplinks are present */
    if (this.mapRef.nativeElement.DeepLinkService.deepLinkParamIsPresent()) {
      this.deeplinkPresent.next();
    }

    if (this.isMobileView) {
      // Listen for orientation changes
      screen.orientation.addEventListener('change', this.updateOrientationStyles);

      // Listen for mobile legend scrolling
      const scrollLeftButton = document.getElementById('scroll-left');
      const scrollRightButton = document.getElementById('scroll-right');

      scrollLeftButton.addEventListener('click', () => this.scrollLegend('left'));
      scrollRightButton.addEventListener('click', () => this.scrollLegend('right'));

      // format the legend horizontally
      this.legendRef.nativeElement.setHorizontalFormat();
      this.isLegendReady = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.toggleLegendWindow();
      }, 10);

      this.checkScrollArrows(); // Check on initialization
      const legendScroll = document.querySelector('.legend-scroll') as HTMLElement;
      if (legendScroll) {
        legendScroll.addEventListener('scroll', () => this.checkScrollArrows());
        window.addEventListener('resize', () => this.checkScrollArrows());
      }

      // first timer tour
      const currentVersion = this.config.version;
      const visitedVersion = localStorage.getItem('visitedVersion');
      const isFirstVisit = !visitedVersion || visitedVersion !== currentVersion;
      if (isFirstVisit) {
        localStorage.setItem('visitedVersion', currentVersion);
        this.startTour();
      }

      await this.setUpCurrentLocation();
    }

    this.identifyRef.nativeElement.addEventListener('featureUpdated', (event) => {
      const existingLinkBtn = document.getElementById('report-links');
      if (existingLinkBtn) {
        existingLinkBtn.remove();
      }
      this.updateCustomIdentifyWindow(event.detail);
    });

  }

  ngOnDestroy() {
    this.mapRef.nativeElement.disposeMap();
  }

  async setBaseMap() {
    await this.mapRef.nativeElement.ready;
    await this.mapRef.nativeElement.setBasemapsFromConfig(this.defaultBasemap);
  }

  async constructExtent() {
    await this.extentRef.nativeElement.ready;
    for (const extentConfig of this.extentConfigs) {
      this.extentRef.nativeElement.addExtentConfiguration(extentConfig);
    }
  }

  async setupBasemapSwitcher() {
    await this.basemapSwitcherRef.nativeElement.ready;
    this.basemapSwitcherRef.nativeElement.addBasemaps(this.basemapList, this.basemapConfigs);
  }

  openAOI(isStartup = false) {
    this.activateAOITab.next();
    if (!this.isMobileView || (this.isMobileView && !isStartup)) {
      this.aoiWindowRef.nativeElement.open();
    }
  }

  closeAOIWindow() {
    this.aoiWindowRef.nativeElement.close();
  }

  AOISelected(aoiType: string[]) {
    this.closeAOIWindow();
    if (!this.isMobileView && !this.legendWindowRef.nativeElement.isOpened) {
      this.legendWindowRef.nativeElement.open();
    } else if (this.isMobileView && !this.showMobileLegend) {
      this.showMobileLegend = true;
      this.toggleMobileLegend();
    }
    const loadedLayers = this.mapRef.nativeElement.LayerService.mapLayers;
    for (const featureLayer of loadedLayers.feature) {
      const foundId = ['34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45'].filter(
        x => featureLayer.mapcoreProps.url.includes(x));
      if (foundId.length > 0) {
        featureLayer.mapcoreProps.visible = true;
        featureLayer.visible = true;
      }
    }

    if (this.aoiTypesWithReport.includes(aoiType[0])) {
      this.enableConditionReportBtn = true;
    } else {
      this.enableConditionReportBtn = false;
    }

    /* change county aoi label to name instead of number value */
    if (aoiType[0] === 'county') {
      const countyLayerIndex: number = loadedLayers.mapImage.findIndex(l => l.mapcoreProps.description === 'PA counties');
      if (countyLayerIndex > -1) {
        const lowerCaseStr = aoiType[1].toLowerCase();
        const countyName = lowerCaseStr.charAt(0).toUpperCase() + lowerCaseStr.slice(1);
        loadedLayers.mapImage[countyLayerIndex].mapcoreProps.name = 'County: ' + countyName;
      }
    }

    this.mapRef.nativeElement.LayerService.refreshLayers();
    this.aoiGo.next();
  }

  async getAoi() {
    const aoiLayer = this.mapRef.nativeElement.LayerService.getAoiLayers();
    if (aoiLayer.length) {
      const aoiExtent = await this.mapRef.nativeElement.LayerService.getLayerExtent(aoiLayer[0]);
      return aoiExtent;
    }
  }

  toggleLegendWindow() {
    if (!this.isMobileView) {
      if (this.legendWindowRef.nativeElement.isOpened) {
        this.legendWindowRef.nativeElement.close();
      } else {
        this.legendWindowRef.nativeElement.open();
      }
    } else {
      this.showMobileLegend = !this.showMobileLegend;
      if (!this.showMobileLegend) {
        this.legendRef.nativeElement.collapseAll();
      }
      this.toggleMobileLegend();
    }
  }

  async loadMapLayers() {
    this.layerService = this.mapRef.nativeElement.LayerService;
    const bridgeConditionsLayerSources = this.config.layerURLs.bridgeConditionsLayers;
    const bridgeConditionsLayerSourcesArray = Object.keys(bridgeConditionsLayerSources).map((key) => bridgeConditionsLayerSources[key]);

    for (const source of bridgeConditionsLayerSourcesArray) {
      try {
        if (typeof (source) === 'string') {
          const layerProps = await this.layerService.getExternalServiceProperties({ url: source });
          layerProps.visible = false;
          await this.layerService.addExternalLayersToMap(layerProps);
        } else if (typeof (source) === 'number') {
          const layerSettings: any = {
            id: source,
            visible: false
          };
          await this.layerService.addMapcoreLayersToMap([layerSettings]);
        }
      } catch (error) {
        // this.loggingService.Log(`Error Loading External Layer '${selected.name}'`, error, LogLevel.Error);
      }
    }
  }

  openStreetView(e: Event) {
    const CRS: { countyCode: string, offset: string, segmentNumber: string, stateRouteNumber: string } = {
      countyCode: this.selectedCustomFeature.county,
      stateRouteNumber: this.selectedCustomFeature.route,
      segmentNumber: this.selectedCustomFeature.segment,
      offset: this.selectedCustomFeature.offset
    };
    this.openSV.next(CRS);
  }

  openShareWindow() {
    const aoiLayer = this.mapRef.nativeElement.LayerService.getAoiLayers();
    this.openSW.next(aoiLayer);
  }

  updateCustomIdentifyWindow(feature) {
    if (!feature || !feature.attributes || !feature.layer || !feature.layer.mapcoreProps) {
      return;
    }
    if (!this.isMobileView && !this.customIdentifyWindowRef.nativeElement.isOpened) {
      this.customIdentifyWindowRef.nativeElement.open();
    } else if (this.isMobileView) {
      this.showMobileIdentify = true;
    }

    if (feature.sourceLayer) {
      this.selectedCustomFeature.projectType = 'AOI';
      this.selectedCustomFeature.overallCond = '';
      this.selectedCustomFeature.brKey = '';
      this.selectedCustomFeature.countyName = '';
      this.selectedCustomFeature.municName = '';
      this.selectedCustomFeature.locationName = '';
      this.selectedCustomFeature.featureCarried = '';
      this.selectedCustomFeature.featureIntersected = '';
      this.selectedCustomFeature.owner = '';
      this.selectedCustomFeature.length = '';
      this.selectedCustomFeature.deckArea = '';
      this.selectedCustomFeature.spansNumber = '';
      this.selectedCustomFeature.material = '';
      this.selectedCustomFeature.structureType = '';
      this.selectedCustomFeature.yearBuilt = '';
      this.selectedCustomFeature.postingStatus = '';
      this.selectedCustomFeature.wlSingle = '';
      this.selectedCustomFeature.wlCombo = '';
      this.selectedCustomFeature.wlOther = '';
      this.selectedCustomFeature.deckCondition = '';
      this.selectedCustomFeature.superCond = '';
      this.selectedCustomFeature.subCond = '';
      this.selectedCustomFeature.culvertCond = '';
      this.selectedCustomFeature.planningPartner = '';
      this.selectedCustomFeature.avgDailyTraffic = '';
      this.selectedCustomFeature.oneMapUrl = '';

      this.selectedCustomFeature.county = '';
      this.selectedCustomFeature.route = '';
      this.selectedCustomFeature.segment = '';
      this.selectedCustomFeature.offset = '';

    } else {
      this.selectedCustomFeature.projectType = feature.attributes.filter(x => x.columnName === 'STATE_LOCAL')[0].value.toString() === 'S' ? 'State' : 'Local';
      this.selectedCustomFeature.overallCond = feature.attributes.filter(x => x.columnName === 'CONDITION')[0].value ?
        feature.attributes.filter(x => x.columnName === 'CONDITION')[0].value.toString() : '';
      this.selectedCustomFeature.brKey = feature.attributes.filter(x => x.columnName === 'BRKEY')[0].value ?
        feature.attributes.filter(x => x.columnName === 'BRKEY')[0].value.toString() : '';
      this.selectedCustomFeature.oneMapUrl = this.config.oneMapUrl + '?Brkey=' + this.selectedCustomFeature.brKey;
      this.selectedCustomFeature.countyName = feature.attributes.filter(x => x.columnName === 'CTY_CODE_DESC')[0].value ?
        feature.attributes.filter(x => x.columnName === 'CTY_CODE_DESC')[0].value.toString() : '';
      this.selectedCustomFeature.municName = feature.attributes.filter(x => x.columnName === 'CTY_MUNI_CODES_DESC')[0].value ?
        feature.attributes.filter(x => x.columnName === 'CTY_MUNI_CODES_DESC')[0].value.toString() : '';
      this.selectedCustomFeature.locationName = feature.attributes.filter(x => x.columnName === 'LOCATION')[0].value ?
        feature.attributes.filter(x => x.columnName === 'LOCATION')[0].value.toString() : '';
      this.selectedCustomFeature.featureCarried = feature.attributes.filter(x => x.columnName === 'FACILITY')[0].value ?
        feature.attributes.filter(x => x.columnName === 'FACILITY')[0].value.toString() : '';
      this.selectedCustomFeature.featureIntersected = feature.attributes.filter(x => x.columnName === 'FEATINT')[0].value ?
        feature.attributes.filter(x => x.columnName === 'FEATINT')[0].value.toString() : '';
      this.selectedCustomFeature.owner = feature.attributes.filter(x => x.columnName === 'OWNER_DESC')[0].value ?
        feature.attributes.filter(x => x.columnName === 'OWNER_DESC')[0].value.toString() : '';
      this.selectedCustomFeature.length = feature.attributes.filter(x => x.columnName === 'BRIDGE_LENGTH')[0].value !== null ?
        feature.attributes.filter(x => x.columnName === 'BRIDGE_LENGTH')[0].value.toString() : '0';
      this.selectedCustomFeature.deckArea = feature.attributes.filter(x => x.columnName === 'DECK_AREA')[0].value !== null ?
        feature.attributes.filter(x => x.columnName === 'DECK_AREA')[0].value.toString() : '0';
      this.selectedCustomFeature.spansNumber = feature.attributes.filter(x => x.columnName === 'MAINSPANS')[0].value !== null ?
        feature.attributes.filter(x => x.columnName === 'MAINSPANS')[0].value.toString() : '0';
      this.selectedCustomFeature.material = feature.attributes.filter(x => x.columnName === 'DEPT_MAIN_MATERIAL_TYPE_DESC')[0].value ?
        feature.attributes.filter(x => x.columnName === 'DEPT_MAIN_MATERIAL_TYPE_DESC')[0].value.toString() : '';
      this.selectedCustomFeature.structureType = feature.attributes.filter(x => x.columnName === 'DEPT_MAIN_STRUC_CONFIG_DESC')[0].value ?
        feature.attributes.filter(x => x.columnName === 'DEPT_MAIN_STRUC_CONFIG_DESC')[0].value.toString() : '';
      this.selectedCustomFeature.yearBuilt = feature.attributes.filter(x => x.columnName === 'YEARBUILT')[0].value ?
        feature.attributes.filter(x => x.columnName === 'YEARBUILT')[0].value.toString() : '';
      this.selectedCustomFeature.postingStatus = feature.attributes.filter(x => x.columnName === 'POST_STATUS_DESC')[0].value ?
        feature.attributes.filter(x => x.columnName === 'POST_STATUS_DESC')[0].value.toString() : '';
      this.selectedCustomFeature.wlSingle =
        feature.attributes.filter(x => x.columnName === 'POST_LIMIT_WEIGHT')[0].value !== 0 &&
          feature.attributes.filter(x => x.columnName === 'POST_LIMIT_WEIGHT')[0].value !== null ?
          feature.attributes.filter(x => x.columnName === 'POST_LIMIT_WEIGHT')[0].value.toString() : 'N/A';
      this.selectedCustomFeature.wlCombo =
        feature.attributes.filter(x => x.columnName === 'POST_LIMIT_COMB')[0].value !== 0 &&
          feature.attributes.filter(x => x.columnName === 'POST_LIMIT_COMB')[0].value !== null ?
          feature.attributes.filter(x => x.columnName === 'POST_LIMIT_COMB')[0].value.toString() : 'N/A';
      this.selectedCustomFeature.wlOther = feature.attributes.filter(x => x.columnName === 'SPEC_RESTRICT_POST')[0].value !== null ?
        feature.attributes.filter(x => x.columnName === 'SPEC_RESTRICT_POST')[0].value.toString() : 'N/A';
      this.selectedCustomFeature.deckCondition = feature.attributes.filter(x => x.columnName === 'DKRATING_DESC')[0].value ?
        feature.attributes.filter(x => x.columnName === 'DKRATING_DESC')[0].value.toString() : '';
      this.selectedCustomFeature.superCond = feature.attributes.filter(x => x.columnName === 'SUPRATING_DESC')[0].value ?
        feature.attributes.filter(x => x.columnName === 'SUPRATING_DESC')[0].value.toString() : '';
      this.selectedCustomFeature.subCond = feature.attributes.filter(x => x.columnName === 'SUBRATING_DESC')[0].value ?
        feature.attributes.filter(x => x.columnName === 'SUBRATING_DESC')[0].value.toString() : '';
      this.selectedCustomFeature.culvertCond = feature.attributes.filter(x => x.columnName === 'CULVRATING_DESC')[0].value ?
        feature.attributes.filter(x => x.columnName === 'CULVRATING_DESC')[0].value.toString() : '';
      this.selectedCustomFeature.planningPartner = feature.attributes.filter(x => x.columnName === 'PLANNING_PARTNER_SHORT_NAME')[0].value ?
        feature.attributes.filter(x => x.columnName === 'PLANNING_PARTNER_SHORT_NAME')[0].value.toString() : '';
      if (feature.attributes.filter(x => x.columnName === 'ADTTOTAL')[0]) {
        this.selectedCustomFeature.avgDailyTraffic = feature.attributes.filter(x => x.columnName === 'ADTTOTAL')[0].value !== null ?
          feature.attributes.filter(x => x.columnName === 'ADTTOTAL')[0].value : '--';
      }

      this.selectedCustomFeature.county = feature.attributes.filter(x => x.columnName === 'CTY_CODE')[0].value ?
        feature.attributes.filter(x => x.columnName === 'CTY_CODE')[0].value.toString() : '';
      this.selectedCustomFeature.route = feature.attributes.filter(x => x.columnName === 'ST_RT_NO')[0].value ?
        feature.attributes.filter(x => x.columnName === 'ST_RT_NO')[0].value.toString() : '';
      this.selectedCustomFeature.offset = feature.attributes.filter(x => x.columnName === 'OFFSET')[0].value ?
        feature.attributes.filter(x => x.columnName === 'OFFSET')[0].value.toString() : '';
      this.selectedCustomFeature.segment = feature.attributes.filter(x => x.columnName === 'SEG_NO')[0].value ?
        feature.attributes.filter(x => x.columnName === 'SEG_NO')[0].value.toString() : '';

      this.selectedCustomFeature.bridgeId = feature.attributes.filter(x => x.columnName === 'BRIDGE_ID')[0].value ?
        feature.attributes.filter(x => x.columnName === 'BRIDGE_ID')[0].value.toString() : '';

      /* penndot near you btn */
      let districtNumber: string = feature.attributes.filter(x => x.columnName === 'DISTRICT')[0].value ?
        feature.attributes.filter(x => x.columnName === 'DISTRICT')[0].value.toString() : '';
      districtNumber = districtNumber.startsWith('0') ? districtNumber.substring(1) : districtNumber;
      this.selectedCustomFeature.penndotNearYouUrl = 'https://www.pa.gov/agencies/penndot/regional-offices/district-' + districtNumber + '.html';
    }
    this.selectedCustomFeature.hasNext = this.identifyRef.nativeElement.getHasNextFeature();
    this.selectedCustomFeature.hasPrev = this.identifyRef.nativeElement.getHasPreviousFeature();

    const countAndIndex: number[] = this.identifyRef.nativeElement.getFeatureCountAndIndex();
    this.featureCount = countAndIndex[0];
    this.curFeatureIndex = countAndIndex[1] + 1;

    if (this.selectedCustomFeature.projectType === 'State' || this.selectedCustomFeature.projectType === 'Local') {
      if (this.selectedCustomFeature.overallCond === 'Poor') {
        this.headerColor = '#CC3232';
      } else if (this.selectedCustomFeature.overallCond === 'Fair') {
        this.headerColor = '#FFA500';
      } else if (this.selectedCustomFeature.overallCond === 'Good') {
        this.headerColor = '#2DC937';
      }

      this.projectTitle = 'Bridge Id: ' + this.selectedCustomFeature.bridgeId;

    } else if (this.selectedCustomFeature.projectType === 'AOI') {
      this.projectTitle = feature.layerName ? feature.layerName : feature.sourceLayer.title;
      this.headerColor = '#000000';
    }
    this.cdr.detectChanges();
  }

  formatCurrency(amount: number): string {
    // Convert number to string and add commas for thousands separator
    const formattedAmount = amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Add currency symbol and return
    return '$' + formattedAmount;
  }

  prevFeature() {
    this.identifyRef.nativeElement.previousFeature();
  }
  nextFeature() {
    this.identifyRef.nativeElement.nextFeature();
  }

  openConditionReport() {
    const aoiLayer = this.mapRef.nativeElement.LayerService.getAoiLayers();
    const url = this.generateReportURL(aoiLayer);
    window.open(url);
  }

  generateReportURL(aoiLayer: any): string {
    let shareStr = this.config.url + 'bridge-conditions-report?';

    if (aoiLayer.length && aoiLayer[0].mapcoreProps && aoiLayer[0].mapcoreProps.query) {
      const selectedAOIQuery: {
        subqueries?: { field: string, operator: string, value: string, subqueries?: any[] }[],
        field: string,
        operator: string,
        value: string
      } = aoiLayer[0].mapcoreProps.query.subqueries[0];

      if (!selectedAOIQuery.subqueries) {
        if (selectedAOIQuery.field === 'COUNTY_NUMBER') {
          shareStr += '&cn=' + selectedAOIQuery.value;
        } else if (selectedAOIQuery.field === 'DISTRICT_NUMBER') {
          shareStr += '&dist=' + selectedAOIQuery.value;
        } else if (selectedAOIQuery.field === 'PLN_PRTNR_SHRTNM') {
          shareStr += '&mporpo=' + selectedAOIQuery.value;
        } else if (selectedAOIQuery.field === 'LEG_DISTRICT_NO') {
          if (aoiLayer[0].mapcoreProps.name.toLowerCase().includes('house')) {
            shareStr += '&houseDistrictNumber=' + selectedAOIQuery.value;
          } else if (aoiLayer[0].mapcoreProps.name.toLowerCase().includes('senate')) {
            shareStr += '&senateDistrictNumber=' + selectedAOIQuery.value;
          } else if (aoiLayer[0].mapcoreProps.name.toLowerCase().includes('congressional')) {
            shareStr += '&congressDistrictNumber=' + selectedAOIQuery.value;
          }
        } else {
          // not a reported aoi
          shareStr = '';
        }
      } else {
        let count = 0;
        for (const query of selectedAOIQuery.subqueries) {
          if (count >= 0) {
            shareStr += '&';
          }

          if (query.field.toUpperCase() === 'CTY_CODE' || query.field.toUpperCase() === 'COUNTY') {
            shareStr += 'cn=' + query.value;
          } else if (query.field.toUpperCase() === 'MUNICIPAL_CODE') {
            shareStr += 'muni=' + query.value;
          } else if (query.field.toUpperCase() === 'ST_RT_NO' ||
            query.field.toUpperCase() === 'PA_CTY_CODE' ||
            query.field.toUpperCase() === 'SCHOOL_DISTRICT') {
            // not a reported aoi
            shareStr = '';
            break;
          } else {
            count--;
          }

          count++;
        }
      }
    } else {
      shareStr += '&aoiType=state';
    }

    return shareStr;
  }

  filterInterstate(routeNumber: string) {
    const loadedLayers = this.mapRef.nativeElement.LayerService.mapLayers;
    for (const featureLayer of loadedLayers.feature) {
      const externalLayerId = featureLayer.id;
      const esriLayer = this.layerService.getEsriLayerByUniqueId(externalLayerId);
      esriLayer.mapcoreProps.filter = 'st_rt_no = \'' + routeNumber + '\'';
      esriLayer.mapcoreProps.query = {
        childTableQuery: {
          operator: 'AND'
        },
        childTableRecord: undefined,
        field: 'st_rt_no',
        joinRecord: undefined,
        joinTableQuery: {
          operator: 'AND'
        },
        operator: '=',
        primaryLayerRMSKeys: undefined,
        value: routeNumber
      };
      esriLayer.definitionExpression = 'st_rt_no = \'' + routeNumber + '\'';
    }
  }
  clearFilter() {
    const loadedLayers = this.mapRef.nativeElement.LayerService.mapLayers;
    for (const featureLayer of loadedLayers.feature) {
      const externalLayerId = featureLayer.id;
      const esriLayer = this.layerService.getEsriLayerByUniqueId(externalLayerId);
      esriLayer.mapcoreProps.filter = '';
      esriLayer.mapcoreProps.query = {
        childTableQuery: {
          operator: 'AND'
        },
        childTableRecord: undefined,
        field: '',
        joinRecord: undefined,
        joinTableQuery: {
          operator: 'AND'
        },
        operator: '=',
        primaryLayerRMSKeys: undefined,
        value: ''
      };
      esriLayer.definitionExpression = '';
    }
  }

  updateOrientationStyles() {
    if (screen.orientation.type.startsWith('portrait')) {
      document.body.style.transform = 'rotate(-90deg)';
      document.body.style.transformOrigin = 'center';
      document.body.style.width = '100vh';
      document.body.style.height = '100vw';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.transform = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.overflow = '';
    }
  }

  checkScrollArrows() {
    const legendScroll = document.querySelector('.legend-scroll') as HTMLElement;

    if (legendScroll) {
      this.showLeftArrow = legendScroll.scrollLeft > 0;
      this.showRightArrow =
        legendScroll.scrollLeft + legendScroll.clientWidth < legendScroll.scrollWidth;
    }
  }

  scrollLegend(direction: 'left' | 'right') {
    const legendScroll = document.querySelector('.legend-scroll') as HTMLElement;
    const scrollAmount = 150;
    if (legendScroll) {
      if (direction === 'left') {
        legendScroll.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        legendScroll.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }

      // Add a short delay to ensure arrows reflect the updated scroll position
      setTimeout(() => this.checkScrollArrows(), 300);
    }
  }

  toggleMobileLegend() {
    const legendContainer = document.getElementById('mLegendContainer');
    if (legendContainer) {
      legendContainer.classList.toggle('active');
    }
  }

  getAOIInfo() {
    // get aoi infor for mobile report/email
    const aoiLayer = this.mapRef.nativeElement.LayerService.getAoiLayers();

    // return data to nav
    if (aoiLayer) {
      const aoiStr = this.generateReportURL(aoiLayer);
      this.returnAOIInfo.next(aoiStr);
    }
  }

  closeMobileIdentify() {
    this.showMobileIdentify = false;
  }

  async setUpCurrentLocation(): Promise<void> {
    const mapView = this.mapRef.nativeElement.mapView;

    // Ensure ArcGIS script is loaded
    await this.loadArcGISScript();

    // Use Dojo's require after ArcGIS script is loaded
    (window as any).require(['esri/widgets/Locate'], (Locate: any) => {
      const locate = new Locate({ view: mapView });
      mapView.ui.add(locate, 'top-right');

      // 'north' the mapview
      locate.on('locate', () => {
        mapView.goTo({ rotation: 0 });
      });
    });
  }

  private loadArcGISScript(): Promise<void> {
    // manually pull in the script for now, could not get it to build locally
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://js.arcgis.com/4.29/';
      script.onload = () => resolve();
      script.onerror = (error) => reject(error);
      document.body.appendChild(script);
    });
  }

  startTour() {
    const mapElement = document.querySelector('#map');
    const mapNav = document.getElementById('map-nav');
    const optionsMenu = document.getElementById('options-menu');
    const legendElement = document.getElementById('mLegendContainer');

    // Manually query the shadow DOM (if applicable)
    const shadowHost = document.querySelector('#shadow-host') as HTMLElement;
    if (shadowHost && shadowHost.shadowRoot) {
      const shadowMapElement = shadowHost.shadowRoot.querySelector('#map');
      // console.log("Found map inside shadow root:", shadowMapElement);
    }

    this.driver.defineSteps([
      {
        element: mapElement,
        popover: {
          title: 'Welcome to PAProjects!',
          description: `This will give you a quick overview of what is new in PAProject's new mobile release.`

        }
      },
      {
        element: mapNav,
        popover: {
          title: 'Bridge Conditions Map',
          description: 'PennDOT is committed to maintaining and improving bridges through bridge preservation activities.  In the long run, preservation saves money by extending the bridge service life, thus deferring the need for major rehabilitation that are costly.  Inspection intervals range from 6 months up to a maximum of 48 months.'

        },
        onNext: this.toggleOptionsMenu // open
      },

      {
        element: optionsMenu,
        popover: {
          title: 'Options Menu',
          description: 'From this menu you can Select an Area of Interest (AOI) to view on the map. After choosing your AOI, a new Summary Report option will appear here. Select that option to opt in for an email with the AOIs construction summary data. Select Contact & About Map to learn more about each.'
        },
        onNext: this.toggleOptionsMenu, // close
        onPrevious: this.toggleOptionsMenu // close
      },
      {
        element: legendElement,
        popover: {
          title: 'Legend',
          description: 'The Legend will list the features appearing in the map. It will allow you to toggle on/off each item. Should the items expand past the end of the screen, the legend will gain arrows to scroll through the list of legend items. You may click the blue legend button to toggle showing/hiding the legend.'
        },
        onPrevious: this.toggleOptionsMenu // open
      },
      {
        element: mapElement,
        popover: {
          title: 'Map Buttons',
          description: `The map host two buttons. In the top left, select the basemap button to look through a list of basemaps and choose a new basemap. In the top right, select the Find My Location button to zoom to your device's location.`

        }
      }
      // Add more steps as needed
    ]);

    // Start the tour
    this.driver.start();
  }

  toggleOptionsMenu = () => {
    this.openOM.next();
  }
}
